import React from 'react'
import moment, { Moment } from 'moment'
import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'

import { IPromotion, IApplyPromotion } from '../../../../types'

import UIMessages from '../../../../utils/ui-messages'
import { Input, Select } from '@mobiscroll/react-latest'
import { Datepicker, MbscCalendarEvent } from '@mobiscroll/react'

interface IApplyPromotionFormProps {
  userId: string
  onSubmit: (promotion: IApplyPromotion) => Promise<void>
  promotions: IPromotion[]
  onCancel: () => void
}

interface IFormValues {
  promotionId: string
  expiresAt: string
  maxRedemptions: number
}

interface IFormProps {
  promotions: IPromotion[]
  onCancel: () => void
}

const InnerForm = (props: IFormProps & FormikProps<IFormValues>) => {
  const { touched, errors, isSubmitting } = props

  const promotionOptions: Array<{ text: string; value: string }> = props.promotions.map(promotion => ({
    text: promotion.title,
    value: promotion.id
  }))

  return (
    <Form className='block'>
      <div className='col-12 mt2'>
        <Field name='promotionId'>
          {({ field, form }: FieldProps) => {
            const onChange = (e?: { value: string }) => {
              if (e) {
                form.setFieldValue(field.name, e.value)
                let promotion: IPromotion | undefined
                if (!touched.maxRedemptions) {
                  promotion = props.promotions.find(({ id }) => id === e.value)
                  if (promotion && promotion.maxRedemptions) {
                    form.setFieldValue('maxRedemptions', promotion.maxRedemptions)
                  }
                }

                if (!touched.expiresAt) {
                  if (promotion) {
                    promotion = props.promotions.find(({ id }) => id === e.value)
                  }

                  if (promotion && promotion.expiresAt) {
                    form.setFieldValue('expiresAt', moment(promotion.expiresAt).toString())
                  } else {
                    form.setFieldValue('expiresAt', null)
                  }
                }
              } else {
                form.setFieldValue(field.name, '')
              }
            }

            const getPromotionTypeValue = () => {
              const promotion = promotionOptions.find(({ value }) => value === field.value)

              if (promotion) {
                return promotion.text
              } else {
                return ''
              }
            }

            return (
              <Select
                value={getPromotionTypeValue()}
                onClose={onChange}
                data={promotionOptions}
                touchUi={false}
                theme='ios'
                themeVariant='light'
                inputStyle='box'
                label={UIMessages.promotions.promotionTypeLabel}
                labelStyle='floating'
              />
            )
          }}
        </Field>
        {touched.promotionId && errors.promotionId && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.promotionId}</span>
        )}
      </div>

      <div className='col-12 mt1'>
        <Field name='expiresAt'>
          {({ field, form }: FieldProps) => {
            const onChange = (date: MbscCalendarEvent) => {
              form.setFieldValue(field.name, date ? moment(date.value).toString() : undefined)
            }

            const promotion: IPromotion | undefined = props.promotions.find(({ id }) => id === form.values.promotionId)
            return (
              <div className='ios-datepicker promotion' style={{ flex: 1 }}>
                <div className='datepicker-label'>{UIMessages.promotions.expiresAtLabel}*</div>
                <div>
                  <Datepicker
                    controls={['calendar']}
                    dateFormat='MM/DD/YYYY'
                    max={promotion && promotion.expiresAt ? moment(promotion.expiresAt) : undefined}
                    min={moment().subtract(100, 'years')}
                    value={form.values[field.name] ? moment.utc(form.values[field.name]) : null}
                    onChange={onChange}
                    theme='ios'
                    themeVariant='light'
                    inputStyle='box'
                    labelStyle='floating'
                    touchUi={false}
                    display='anchored'
                    calendarScroll='horizontal'
                  />
                </div>
              </div>
            )
          }}
        </Field>
        {touched.expiresAt && errors.expiresAt && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.expiresAt}</span>
        )}
      </div>

      <div className='col-12 mt1'>
        <Field name='maxRedemptions'>
          {({ field, form }: FieldProps) => {
            const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
              form.setFieldValue(field.name, event.target.value)
            }

            const promotion: IPromotion | undefined = props.promotions.find(({ id }) => id === form.values.promotionId)
            return (
              <Input
                type='number'
                className='mobiscroll-input form'
                onChange={onChange}
                onKeyPress={(event: React.KeyboardEvent) => {
                  if (!(event.charCode >= 48 && event.charCode <= 57)) {
                    event.preventDefault()
                  }
                }}
                placeholder='0'
                value={form.values[field.name] ? form.values[field.name] : null}
                min={0}
                theme='ios'
                themeVariant='light'
                inputStyle='box'
                label={UIMessages.promotions.maxRedemptionsLabel}
                labelStyle='floating'
              />
            )
          }}
        </Field>
        {touched.maxRedemptions && errors.maxRedemptions && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.maxRedemptions}</span>
        )}
      </div>

      <div className='flex items-center justify-end mt3'>
        <button
          type='button'
          onClick={props.onCancel}
          disabled={isSubmitting}
          className='custom-btn mbsc-clear-btn mr2'
        >
          {UIMessages.common.cancel}
        </button>
        <button type='submit' className='custom-btn mbsc-green-btn'>
          {UIMessages.common.save}
        </button>
      </div>
    </Form>
  )
}

const ApplyPromotionForm = withFormik<IApplyPromotionFormProps, IFormValues>({
  mapPropsToValues: (props: IApplyPromotionFormProps): IFormValues => {
    return {
      promotionId: '',
      maxRedemptions: 0,
      expiresAt: ''
    }
  },
  validate: (values: IFormValues): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}

    if (!values.promotionId) {
      errors.promotionId = 'Required'
    }

    if (!Number.isInteger(values.maxRedemptions) || values.maxRedemptions <= 0) {
      errors.maxRedemptions = 'Invalid'
    }

    return errors
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    setSubmitting(true)

    const promotion: IApplyPromotion = {
      userId: props.userId,
      promotionId: values.promotionId,
      maxRedemptions: values.maxRedemptions,
      expiresAt: values.expiresAt
    }

    if (values.expiresAt) {
      promotion.expiresAt = values.expiresAt
    }

    await props.onSubmit(promotion)

    setSubmitting(false)
  }
})(InnerForm)

export default ApplyPromotionForm



// WEBPACK FOOTER //
// ./src/components/riders/riderdetails/promotions/ApplyPromotionForm.tsx